import validator from "validator";
import { Constants } from "src/billinglatest/constants";
import type { PixiAddress } from "../../../hooks/usePixiWizardState";
import { isShippingSupported } from "./supportedShippingCountries";

/** Fields on {@link PixiAddress} that can carry a validation error. */
export type AddressField = keyof PixiAddress;

/** Why a field failed validation; maps 1:1 to a localized message. */
export type AddressErrorReason =
  | "required"
  | "email_invalid"
  | "shipping_unsupported"
  | "postal_invalid"
  | "phone_invalid"
  // Backend address-verification (billing-client `/addresses/verify`) reasons,
  // surfaced on Continue rather than synchronously.
  | "postal_mismatch"
  | "state_mismatch"
  | "address_not_found";

export type AddressErrors = Partial<Record<AddressField, AddressErrorReason>>;

/**
 * A per-field problem returned by the backend address verifier, tagged with the
 * form it came from so the wizard can render it inline on the right field.
 */
export interface AddressVerifyIssue {
  form: "billing" | "shipping";
  field: AddressField;
  reason: AddressErrorReason;
}

/**
 * Email format check. Intentionally permissive — matches `something@domain.tld`
 * and rejects obvious non-emails like `sdfs` (the Figma error example).
 */
export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Optional-phone plausibility: count digits only (ignore spaces, +, (), -).
 * Accepts 7-15 digits, the E.164 range, so valid landlines and international
 * numbers pass while junk and truncated numbers are rejected.
 */
export const isPlausiblePhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

/** Whether the given country uses a state/province dropdown (US, CA). */
export const countryHasState = (countryCode: string): boolean =>
  Object.keys(Constants.States).includes(countryCode);

/** Supported shipping countries `validator` does not cover, with a local regex. */
const POSTAL_FALLBACK: Record<string, RegExp> = {
  MC: /^980\d{2}$/, // Monaco: 980xx
};

/**
 * Postal-code format check for the given country. Lenient by design: empty
 * input (handled by the required check) and countries `validator` does not
 * know both pass, so we never block a real address we cannot judge.
 */
export const isValidPostalFormat = (
  postalCode: string,
  country: string,
): boolean => {
  const code = postalCode.trim();
  if (!code || !country.trim()) return true;

  const fallback = POSTAL_FALLBACK[country];
  if (fallback) return fallback.test(code);

  try {
    return validator.isPostalCode(code, country as validator.PostalCodeLocale);
  } catch {
    return true; // country not in validator's locale list → do not flag
  }
};

interface ValidateAddressOptions {
  /** Require a state for state-countries (US/CA); set for billing and shipping. */
  requireState: boolean;
  /** Shipping requires a country we actually ship hardware to. */
  requireShippingSupported: boolean;
}

/**
 * Compute per-field validation errors for an address. Required fields are
 * first name, email, country, street, city and postal code (company, second
 * name, phone and building are optional). Email must additionally be a valid
 * format. A state is additionally required when the country has states (US/CA)
 * and `requireState` is set; shipping additionally requires a supported country.
 */
export const validateAddress = (
  address: PixiAddress,
  options: ValidateAddressOptions,
): AddressErrors => {
  const errors: AddressErrors = {};

  if (!address.firstName.trim()) errors.firstName = "required";

  if (!address.email.trim()) errors.email = "required";
  else if (!isValidEmail(address.email)) errors.email = "email_invalid";

  if (!address.country.trim()) errors.country = "required";

  if (!address.street.trim()) errors.street = "required";

  if (!address.city.trim()) errors.city = "required";

  if (!address.postalCode.trim()) {
    errors.postalCode = "required";
  } else if (!isValidPostalFormat(address.postalCode, address.country)) {
    errors.postalCode = "postal_invalid";
  }

  if (address.phone.trim() && !isPlausiblePhone(address.phone)) {
    errors.phone = "phone_invalid";
  }

  if (
    options.requireState &&
    countryHasState(address.country) &&
    !address.state.trim()
  ) {
    errors.state = "required";
  }

  if (
    options.requireShippingSupported &&
    address.country.trim() &&
    !isShippingSupported(address.country)
  ) {
    errors.country = "shipping_unsupported";
  }

  return errors;
};

export const hasErrors = (errors: AddressErrors): boolean =>
  Object.values(errors).some(Boolean);

/** Field render order in {@link AddressForm}, used to pick the topmost error. */
export const ADDRESS_FIELD_ORDER: AddressField[] = [
  "firstName",
  "secondName",
  "company",
  "email",
  "phone",
  "country",
  "state",
  "street",
  "building",
  "city",
  "postalCode",
];

/**
 * The verify issue whose field appears first on screen — billing before
 * shipping, then by field render order — so Continue can scroll to and focus the
 * topmost failed field. Returns undefined for an empty list.
 */
export const firstIssueTarget = (
  issues: AddressVerifyIssue[],
): AddressVerifyIssue | undefined =>
  [...issues].sort(
    (a, b) =>
      (a.form === "billing" ? 0 : 1) - (b.form === "billing" ? 0 : 1) ||
      ADDRESS_FIELD_ORDER.indexOf(a.field) -
        ADDRESS_FIELD_ORDER.indexOf(b.field),
  )[0];
